/* eslint-disable no-await-in-loop */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-extraneous-dependencies */

import { useState, useEffect } from "react";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { process } from "@progress/kendo-data-query";
import Loader from "ui/page-loader";
import { BreadCrumb } from "ui/bread-crumbs";
import { PATHS } from "routes/paths";
import { RefreshCcw } from "lucide-react";
import GeneratedReportsFilter from "./common/GeneratedReportsFilter";
import { useGetServiceReportRequests, useGetReports } from "services/reports";
import {
  useLookupCashCenters,
  useLookupCustomers,
  useLookupAllMachinesServicePoints,
} from "services/lookups";
import { Toast } from "@chakra-ui/react";
import http from "services/http-client";

const DEFAULT_FILTERS = {
  customerId: "",
  assetId: "",
  cashCenterId: "",
  activityType: "",
  fromDate: "",
  toDate: "",
};

export default function ServiceTicketReport() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [reports, setReports] = useState([]);
  const [dataState, setDataState] = useState({
    sort: [{ field: "requestedAt", dir: "desc" }],
    take: 100,
    skip: 0,
  });
  const API_URL = process.env.REACT_APP_API_URL;

  const [customersList, setCustomersList] = useState([]);
  const [cashCentersList, setCashCentersList] = useState([]);
  const [assetOptions, setAssetOptions] = useState([]);

  /* =======================
     Error handling
  ======================== */
  const showError = (title, error) => {
    Toast({
      variant: "destructive",
      title,
      description: error?.message || "Something went wrong",
    });
  };

  /* =======================
     API hooks
  ======================== */
  const {
    mutate: getServiceReportRequests,
    data: reportsRes,
    isPending: gettingReports,
  } = useGetServiceReportRequests();

  const { mutate: getCustomers, data: customersRes } = useLookupCustomers();
  const { mutate: getCashCenters, data: cashCentersRes } =
    useLookupCashCenters();
  const { mutate: getLocations, data: locationsRes } =
    useLookupAllMachinesServicePoints();

  const { mutate: getReports } = useGetReports();

  const decodeHtml = (value = "") => {
    if (!value) return "";
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
  };

  const normalizeForCell = (value = "") =>
    decodeHtml(value)
      .replace(/\r\n|\r|\n/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const preserveForTooltip = (value = "") => decodeHtml(value).trim();

  useEffect(() => {
    getCustomers({
      onError: (err) => showError("Failed to load customers", err),
    });

    getCashCenters({
      onError: (err) => showError("Failed to load cash centers", err),
    });

    getServiceReportRequests(filters, {
      onError: (err) => showError("Failed to load service ticket reports", err),
    });
  }, []);

  useEffect(() => {
    if (customersRes?.data) {
      setCustomersList(customersRes.data);
    }
  }, [customersRes]);

  useEffect(() => {
    if (cashCentersRes?.data) {
      setCashCentersList(cashCentersRes.data);
    }
  }, [cashCentersRes]);

  useEffect(() => {
    if (locationsRes?.data) {
      setAssetOptions(
        locationsRes.data.map((loc) => ({
          label: loc.locationId,
          value: loc.servicePointId,
        })),
      );
    }
  }, [locationsRes]);

  useEffect(() => {
    if (reportsRes?.data) {
      setReports(reportsRes.data);
    }
  }, [reportsRes]);

  const handleRefresh = () => {
    getServiceReportRequests(filters, {
      onError: (err) => showError("Failed to refresh reports", err),
    });
  };

  const handleApplyFilters = (appliedFilters) => {
    setFilters(appliedFilters);

    getReports(appliedFilters, {
      onSuccess: () => {
        handleRefresh();
      },
      onError: (err) => showError("Failed to fetch filtered reports", err),
    });
  };

  const handleDownload = async (reportUrl) => {
    if (!reportUrl) return;

    const urls = reportUrl
      .split(";")
      .map((u) => u.trim())
      .filter(Boolean);

    for (const url of urls) {
      const fileName = url.split("/").pop() || "report";

      try {
        // Build proper API endpoint based on environment
        const downloadUrl = `${API_URL}/common/download/report?path=${encodeURIComponent(url)}`;

        // Axios request with blob responseType
        const response = await http.get(downloadUrl, { responseType: "blob" });

        const blob = new Blob([response.data], { type: response.data.type });
        const objectUrl = URL.createObjectURL(blob);

        // Create temporary anchor to download
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = fileName;
        anchor.target = "_blank";
        anchor.rel = "noopener";
        anchor.style.display = "none";

        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        URL.revokeObjectURL(objectUrl);
      } catch (err) {
        showError("Download failed", err);
      }
    }
  };

  function ReportDetailsCell(props) {
    const { parameters = {}, fromDate, toDate } = props.dataItem;

    const getTexts = (arr = []) =>
      arr.length ? arr.map((x) => x.text).join(", ") : null;

    const details = [];

    const cashCenters = getTexts(parameters.CashCenter);
    if (cashCenters) details.push(`CashCenter: ${cashCenters}`);

    const customers = getTexts(parameters.Customer);
    if (customers) details.push(`Customer: ${customers}`);

    const assets = getTexts(parameters.Asset);
    if (assets) details.push(`Asset: ${assets}`);

    const activities = getTexts(parameters.ActivityType);
    if (activities) details.push(`ActivityType: ${activities}`);

    const dateCriteria = parameters.DateFilterCriteria?.[0]?.text;
    if (dateCriteria) details.push(`DateFilterCriteria: ${dateCriteria}`);

    if (fromDate && toDate) {
      details.push(`DateRange: [${fromDate} - ${toDate}]`);
    }

    // Nothing to show
    if (!details.length) {
      return <td>-</td>;
    }

    const displayText = details.join(" | ");
    const tooltipText = details.join("\n");

    const truncated =
      displayText.length > 100
        ? `${displayText.slice(0, 100)}...`
        : displayText;

    return (
      <td>
        <span
          className="block max-w-[360px] truncate cursor-help"
          title={tooltipText}
        >
          {truncated}
        </span>
      </td>
    );
  }

  return (
    <div className="box-border px-2 sm:px-4">
      {gettingReports ? <Loader /> : null}

      <BreadCrumb
        className="mb-4"
        items={[
          { to: PATHS.dashboard, text: "Dashboard" },
          { text: "Ticket Reports" },
        ]}
      />

      <Card className="rounded-xl shadow-sm">
        <CardContent className="p-4 sm:p-6 space-y-6">
          <div className="mb-2 flex items-center justify-between gap-2">
            <CardHeader className="p-0">
              <CardTitle size="md">Generated Reports</CardTitle>
            </CardHeader>

            <div className="flex items-center gap-2">
              <GeneratedReportsFilter
                customersList={{ data: customersList }}
                cashCentersList={{ data: cashCentersList }}
                assetOptions={assetOptions}
                reportType="ServiceTicketsReport"
                onApply={handleApplyFilters}
                onSuccess={handleRefresh}
              />

              <Button
                type="button"
                variant="secondary"
                onClick={handleRefresh}
                title="Refresh List"
              >
                <RefreshCcw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="relative -mx-4 sm:mx-0 overflow-x-auto">
            <div className="min-w-[1200px]">
              <Grid
                data={process(reports, dataState)}
                {...dataState}
                sortable
                filterable
                pageable={{ pageSizes: [50, 100, 200] }}
                navigatable
                className="list-tables-v1"
                onDataStateChange={(e) =>
                  setDataState({ ...e.dataState, skip: 0 })
                }
              >
                <GridColumn
                  title="Requested At"
                  field="requestedAt"
                  width="200px"
                  className="text-center"
                  headerClassName="text-center"
                  // cell={(props) => (
                  //   <td className="text-center">
                  //     {props.dataItem.requestedAt
                  //       ? new Date(props.dataItem.requestedAt).toLocaleString()
                  //       : "-"}
                  //   </td>
                  // )}
                />

                <GridColumn
                  title="Filter Criteria"
                  minWidth="380px"
                  cell={ReportDetailsCell}
                />

                <GridColumn
                  title="Description"
                  field="emailMessage"
                  minWidth="350px"
                  cell={(props) => {
                    const raw = props.dataItem.emailMessage || "";
                    const cellText = normalizeForCell(raw);
                    const tooltipText = preserveForTooltip(raw);

                    const display =
                      cellText.length > 80
                        ? `${cellText.slice(0, 80)}...`
                        : cellText;

                    return (
                      <td>
                        <span
                          className="block max-w-[320px] truncate cursor-help"
                          title={tooltipText}
                        >
                          {display || "-"}
                        </span>
                      </td>
                    );
                  }}
                />
                <GridColumn
                  title="Completed At"
                  field="completedAt"
                  width="200px"
                  className="text-center"
                  headerClassName="text-center"
                />
                <GridColumn
                  title="Status"
                  field="status"
                  width="140px"
                  className="text-center"
                  headerClassName="text-center"
                />
                <GridColumn
                  title="Action"
                  width="150px"
                  filterable={false}
                  headerClassName="text-center"
                  cell={(props) => {
                    const { status, reportUrl } = props.dataItem;

                    if (status !== "Completed" || !reportUrl) {
                      return <td className="text-center">-</td>;
                    }

                    return (
                      <td className="text-center">
                        <button
                          type="button"
                          onClick={() => handleDownload(reportUrl)}
                          className="text-primary font-medium hover:underline"
                        >
                          Download
                        </button>
                      </td>
                    );
                  }}
                />
              </Grid>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
